import { createSelector } from 'reselect'
import {
  getTourV2IdForCheckoutItem,
  isBedbankItem,
  isInstantBookingLEHotelItem,
  isLEHotelItem,
  isTourV2Item,
} from 'lib/checkout/checkoutUtils'
import { subDays } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getInsuranceTotals } from '../insurance'
import getAllItemsDiscountTotals from '../getAllItemsDiscountTotals'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import getAllItemViews from 'checkout/selectors/view/getAllItemViews'
import { getAllOffers } from 'selectors/offerSelectors'
import {
  AGENT_HUB_PAYMENT_MODE,
  LPC_50_DEFAULT_SUPPLIER_PAYMENT_DAYS,
  PAYMENT_SCHEDULE_TYPE,
  STATIC_PAYMENT_SCHEDULE_TEMPLATE_ID,
} from 'constants/payment'
import getExperienceItemsView from 'checkout/selectors/view/itemViews/getExperienceItemsView'
import isPaymentScheduleAvailable from './isPaymentScheduleAvailable'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { getAccommodationBreakdownView } from 'checkout/selectors/view/accommodation'
import getLoyaltyLoungePassItemViews from 'checkout/selectors/view/itemViews/getLoyaltyLoungePassItemViews'
import getLoyaltyTransferItemView from 'checkout/selectors/view/itemViews/getLoyaltyTransferItemView'
import { buildPaymentScheduleItems, calculateGiftCardAmount } from './paymentScheduleItemUtils'
import { isLpcHotelOffer, isLppHotelOffer } from 'lib/offer/offerTypes'
import { isLpc50DepositExperimentVariantForces50Schedule } from './lpc50PercentDepositPaymentSchedule'
import { isLpp50DepositExperimentVariantForces50Schedule } from './lpp50PercentDepositPaymentSchedule'

function getScheduleData(
  paymentScheduleInfo: App.PaymentScheduleInfo | undefined,
  scheduleType: PAYMENT_SCHEDULE_TYPE,
  rebookingData?: {
    templateId: string | undefined
    frequency: 'weekly' | 'fortnightly' | 'monthly' | undefined
    supplierPaymentDays: number | undefined
  },
  useLpc50StaticOverride?: boolean,
  useLpp50StaticOverride?: boolean,
): { templateId: string | undefined; templateIds?: Array<string>; supplierPaymentNumberOfDays: number | undefined } | undefined {
  // When rebooking is active, only the matching schedule type should return data
  // Static rebooking (no frequency): only static selector returns data
  // Dynamic rebooking (has frequency): only dynamic selector returns data
  if (rebookingData?.templateId) {
    const isRebookingDynamic = !!rebookingData.frequency
    const isScheduleDynamic = scheduleType === PAYMENT_SCHEDULE_TYPE.DYNAMIC

    // Return undefined for non-matching schedule type to make that selector return null
    if (isRebookingDynamic !== isScheduleDynamic) {
      return undefined
    }

    return {
      templateId: rebookingData.templateId,
      supplierPaymentNumberOfDays: rebookingData.supplierPaymentDays,
    }
  }

  // LPC and LPP 50% experiment variants: use static 50% template (supplier days from offer or default).
  if ((useLpc50StaticOverride || useLpp50StaticOverride) && scheduleType === PAYMENT_SCHEDULE_TYPE.STATIC) {
    const supplierPaymentNumberOfDays =
      paymentScheduleInfo?.dynamicSchedule?.supplierPaymentNumberOfDays ??
      paymentScheduleInfo?.staticSchedule?.supplierPaymentNumberOfDays ??
      LPC_50_DEFAULT_SUPPLIER_PAYMENT_DAYS
    return {
      templateId: STATIC_PAYMENT_SCHEDULE_TEMPLATE_ID,
      supplierPaymentNumberOfDays,
    }
  }
  if (!paymentScheduleInfo) {
    return undefined
  }

  const schedule = scheduleType === PAYMENT_SCHEDULE_TYPE.STATIC
    ? paymentScheduleInfo.staticSchedule
    : paymentScheduleInfo.dynamicSchedule

  return schedule
}

const createPaymentScheduleSelector = (scheduleType: PAYMENT_SCHEDULE_TYPE) => createSelector(
  (state: App.State) => isPaymentScheduleAvailable(state),
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => state.checkout.commissionData?.itemsCommission,
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => state.checkout.payment.useGiftCard,
  (state: App.State) => state.redeemedGiftCards.cards,
  (state: App.State) => state.checkout.payment.paymentScheduleRebookingId,
  (state: App.State) => state.checkout.payment.paymentScheduleRebookingTemplateId,
  (state: App.State) => state.checkout.payment.paymentScheduleRebookingFrequency,
  (state: App.State) => state.checkout.payment.paymentScheduleRebookingSupplierPaymentDays,
  (state: App.State) => getLoyaltyLoungePassItemViews(state),
  (state: App.State) => getLoyaltyTransferItemView(state),
  (state: App.State) => isLpc50DepositExperimentVariantForces50Schedule(state),
  (state: App.State) => isLpp50DepositExperimentVariantForces50Schedule(state),
  (
    isPaymentScheduleAvailable,
    insuranceTotals,
    discountTotalToCartItemsMap,
    checkoutWithMemberPrice,
    cartItems,
    offers,
    itemViews,
    experienceItemViews,
    accommodationBreakdownView,
    itemsCommission,
    agentHubPaymentMode,
    bedbankOfferRates,
    currency,
    isCreditApplied,
    isGiftCardApplied,
    redeemedGiftCards,
    paymentScheduleRebookingId,
    paymentScheduleRebookingTemplateId,
    paymentScheduleRebookingFrequency,
    paymentScheduleRebookingSupplierPaymentDays,
    loyaltyLoungePassItemViews,
    loyaltyTransferItemViews,
    lpc50VariantForces50Schedule,
    lpp50VariantForces50Schedule,
  ): App.Checkout.PaymentScheduleRequestInfo | null => {
    if (!isPaymentScheduleAvailable && !(lpp50VariantForces50Schedule && scheduleType === PAYMENT_SCHEDULE_TYPE.STATIC)) {
      return null
    }

    if (scheduleType === PAYMENT_SCHEDULE_TYPE.DYNAMIC && (lpc50VariantForces50Schedule || lpp50VariantForces50Schedule)) {
      return null
    }

    const giftCardAmount = calculateGiftCardAmount(redeemedGiftCards)

    const shouldUseAgentHubCommission = isAgentHubEnabled && agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_NET_AMOUNT

    const getPaymentScheduleInfoFromOffer = (item: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem): App.PaymentScheduleInfo | undefined => {
      if (isTourV2Item(item)) {
        const tourV2Offer = offers[getTourV2IdForCheckoutItem(item)] as App.Tours.TourV2Offer
        return tourV2Offer?.paymentScheduleInfo
      } else if (isBedbankItem(item)) {
        const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
        const bedbankRates = bedbankOfferRates[item.offerId]?.[rateKey]
        const rate = bedbankRates?.find(rate => rate.id === item.roomRateId)
        return rate?.paymentScheduleInfo
      } else if (isLEHotelItem(item)) {
        // LPC items carry paymentScheduleInfo from the pricing API (svc-accommodation);
        // flash items fall back to the offer-level paymentScheduleInfo (from public-offer templates).
        if (item.paymentScheduleInfo) {
          return item.paymentScheduleInfo
        }
        const hotelOffer = offers[item.offerId] as App.HotelOffer
        return hotelOffer?.paymentScheduleInfo
      }
    }

    const getStartDateFromItem = (item: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem): string | undefined => {
      if (isTourV2Item(item)) {
        return item.startDate
      } else if (isBedbankItem(item)) {
        return 'checkIn' in item ? item.checkIn : undefined
      } else if (isLEHotelItem(item)) {
        return 'checkIn' in item ? item.checkIn : undefined
      }
      return undefined
    }

    // Define item type configurations with priority order
    const itemTypeConfigs = [
      {
        type: 'tour',
        items: cartItems.filter(isTourV2Item),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
      {
        type: 'bedbank',
        items: cartItems.filter(isBedbankItem),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
      {
        type: 'hotel',
        items: cartItems.filter(isLEHotelItem),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
    ]

    // Find the first item with a template ID across all item types
    let itemWithTemplateId: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem | undefined

    for (const config of itemTypeConfigs) {
      itemWithTemplateId = config.items.find(config.hasPaymentScheduleInfo)
      if (itemWithTemplateId) {
        break
      }
    }

    // LPP50 experiment variant: if no item has a payment schedule template, fall back to
    // any bedbank LPP item — the 50% deposit template will be applied as an override.
    if (!itemWithTemplateId && lpp50VariantForces50Schedule && scheduleType === PAYMENT_SCHEDULE_TYPE.STATIC) {
      itemWithTemplateId = cartItems.filter(isBedbankItem).find(item => isLppHotelOffer(offers[item.offerId]))
    }

    if (!itemWithTemplateId) {
      return null
    }

    const paymentScheduleInfo = getPaymentScheduleInfoFromOffer(itemWithTemplateId)
    const offerForScheduleItem = offers[itemWithTemplateId.offerId] as App.AnyOffer | undefined
    const hotelOfferForScheduleItem = isLEHotelItem(itemWithTemplateId)
      ? (offerForScheduleItem as App.HotelOffer | undefined)
      : undefined
    const useLpc50StaticOverride =
      scheduleType === PAYMENT_SCHEDULE_TYPE.STATIC &&
      !paymentScheduleRebookingId &&
      isLEHotelItem(itemWithTemplateId) &&
      isInstantBookingLEHotelItem(itemWithTemplateId) &&
      isLpcHotelOffer(hotelOfferForScheduleItem) &&
      lpc50VariantForces50Schedule
    const useLpp50StaticOverride =
      scheduleType === PAYMENT_SCHEDULE_TYPE.STATIC &&
      !paymentScheduleRebookingId &&
      isBedbankItem(itemWithTemplateId) &&
      isLppHotelOffer(offerForScheduleItem) &&
      lpp50VariantForces50Schedule

    const schedule = getScheduleData(
      paymentScheduleInfo,
      scheduleType,
      paymentScheduleRebookingId ? {
        templateId: paymentScheduleRebookingTemplateId,
        frequency: paymentScheduleRebookingFrequency ?? undefined,
        supplierPaymentDays: paymentScheduleRebookingSupplierPaymentDays,
      } : undefined,
      useLpc50StaticOverride,
      useLpp50StaticOverride,
    )

    if (!schedule?.templateId) {
      return null
    }

    if (!schedule.supplierPaymentNumberOfDays) {
      return null
    }
    // For bedbank items, we need to get the total from the item view
    if (isBedbankItem(itemWithTemplateId)) {
      const bedbankItemView = itemViews.data.get(itemWithTemplateId.itemId)?.data as App.Checkout.BedbankAccommodationItemView | undefined
      if (!bedbankItemView?.totals?.price) {
        return null
      }
    } else if (isLEHotelItem(itemWithTemplateId)) {
      const hotelItemView = itemViews.data.get(itemWithTemplateId.itemId)?.data as App.Checkout.AccommodationItemView | undefined
      if (!hotelItemView?.totals?.price) {
        return null
      }
    } else if (!itemWithTemplateId.total) {
      return null
    }

    const startDate = getStartDateFromItem(itemWithTemplateId)

    if (!startDate) {
      return null
    }

    const dueDate = subDays(new Date(startDate), schedule.supplierPaymentNumberOfDays)

    const items = buildPaymentScheduleItems(cartItems, {
      offers,
      discountTotalToCartItemsMap,
      checkoutWithMemberPrice,
      insuranceTotals,
      accommodationBreakdownView,
      itemViews,
      experienceItemViews,
      loyaltyLoungePassItemViews,
      loyaltyTransferItemViews,
      agentHubCommission: {
        shouldUse: shouldUseAgentHubCommission,
        itemsCommission,
      },
    })

    if (!items) {
      return null
    }

    return {
      templateId: schedule.templateId,
      ...(schedule.templateIds && schedule.templateIds.length > 1 && { templateIds: schedule.templateIds }),
      bookingInfo: {
        bookingDate: moment().format(ISO_DATE_FORMAT),
        checkinDate: startDate,
        supplierDueDate: moment(dueDate).format(ISO_DATE_FORMAT),
        isCreditApplied,
        isGiftCardApplied,
        giftCardAmount,
        isPaymentScheduleRebooking: !!paymentScheduleRebookingId,
        currency,
        items,
      },
    }
  },
)

export default createPaymentScheduleSelector
